import React, { Fragment, useState } from 'react';
import { TrashIcon, XMarkIcon } from '@heroicons/react/24/outline';
import Button from './Button';
import { useAppDispatch } from '../../hooks';
import { softDeleteSolutionDeployment } from '../../reducers/solutionDeploymentsSlice';
import { useToken } from '../../hooks/useToken';
import { Dialog, DialogPanel, DialogTitle, Transition, TransitionChild } from '@headlessui/react';
import type { ISolutionDeploymentErrorResponse } from '../../interfaces/ISolutionDeployment';
import { sendEvent } from '../../utilities/analytics';

interface DeleteSolutionDeploymentButtonProps {
  deploymentId: number;
  size?: 'small' | 'medium' | 'large' | 'mediumPlus';
  className?: string;
  disabled?: boolean;
}

const DeleteSolutionDeploymentButton: React.FC<DeleteSolutionDeploymentButtonProps> = ({
  deploymentId,
  size = 'large',
  className = '',
  disabled = false,
}) => {
  const [isDeleting, setIsDeleting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const token = useToken();
  const dispatch = useAppDispatch();

  const handleConfirmDelete = async () => {
    setShowConfirmDialog(false);
    setIsDeleting(true);
    setErrorMessage(null);

    sendEvent({ name: 'deployment-delete-start', properties: { deploymentProjectId: deploymentId, workflow: 'new' } });

    try {
      await dispatch(softDeleteSolutionDeployment({ token, deploymentId })).unwrap();
    } catch (error: unknown) {
      console.error('Failed to delete deployment:', error);

      const errorResponse = error as ISolutionDeploymentErrorResponse;

      let errorMsg: string;
      if (errorResponse.message_code === 'ERR_NO_TEARDOWN_PIPELINE') {
        errorMsg = 'No teardown script found for this deployment. The deployment cannot be deleted automatically.';
      } else if (errorResponse.message_code === 'ERR_DEPLOYMENT_ALREADY_DELETING') {
        errorMsg = 'This deployment is already being deleted.';
      } else {
        errorMsg = errorResponse.message ?? (error instanceof Error ? error.message : 'Failed to delete deployment');
      }

      setErrorMessage(errorMsg);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <>
      <div className="flex flex-col">
        <Button
          onClick={() => {
            setShowConfirmDialog(true);
          }}
          intent="danger"
          size={size}
          disabled={disabled || isDeleting}
          loading={isDeleting}
          className={`z-10 ${className}`}
        >
          <TrashIcon className="w-4 h-4 mr-1" />
          {isDeleting ? 'Deleting...' : 'Delete'}
        </Button>
      </div>

      {/* Confirmation Dialog */}
      <Transition show={showConfirmDialog} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-50"
          onClose={() => {
            setShowConfirmDialog(false);
          }}
        >
          <TransitionChild
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </TransitionChild>

          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <TransitionChild
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <DialogPanel className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
                  <div className="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
                    <div className="sm:flex sm:items-start">
                      <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                        <TrashIcon className="h-6 w-6 text-red-600" aria-hidden="true" />
                      </div>
                      <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left flex-1">
                        <DialogTitle as="h3" className="text-base font-semibold leading-6 text-gray-900">
                          Delete Deployment
                        </DialogTitle>
                        <div className="mt-2">
                          <p className="text-sm text-gray-500">
                            Are you sure you want to delete this deployment? This will trigger the teardown pipeline and
                            cannot be undone.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6 gap-2">
                    <Button size="large" intent="danger" className="flex items-center" onClick={handleConfirmDelete}>
                      Delete
                    </Button>
                    <Button
                      size="large"
                      intent="secondary"
                      className="flex items-center"
                      onClick={() => {
                        setShowConfirmDialog(false);
                      }}
                    >
                      Cancel
                    </Button>
                  </div>
                </DialogPanel>
              </TransitionChild>
            </div>
          </div>
        </Dialog>
      </Transition>

      {/* Error Dialog */}
      <Transition show={!!errorMessage} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-50"
          onClose={() => {
            setErrorMessage(null);
          }}
        >
          <TransitionChild
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </TransitionChild>

          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <TransitionChild
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <DialogPanel className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
                  <div className="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
                    <div className="sm:flex sm:items-start">
                      <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                        <XMarkIcon className="h-6 w-6 text-red-600" aria-hidden="true" />
                      </div>
                      <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left flex-1">
                        <DialogTitle as="h3" className="text-base font-semibold leading-6 text-gray-900">
                          Delete Failed
                        </DialogTitle>
                        <div className="absolute right-0 top-0 pr-4 pt-4">
                          <button
                            type="button"
                            className="rounded-md bg-white text-gray-400 hover:text-gray-500"
                            onClick={() => {
                              setErrorMessage(null);
                            }}
                          >
                            <span className="sr-only">Close</span>
                            <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                          </button>
                        </div>
                        <div className="mt-2">
                          <p className="text-sm text-gray-500 whitespace-pre-wrap break-words">{errorMessage}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                    <Button
                      size="large"
                      intent="primary"
                      className="flex items-center"
                      onClick={() => {
                        setErrorMessage(null);
                      }}
                    >
                      Close
                    </Button>
                  </div>
                </DialogPanel>
              </TransitionChild>
            </div>
          </div>
        </Dialog>
      </Transition>
    </>
  );
};

export default DeleteSolutionDeploymentButton;
